import type { GradeCreateSchema } from '@repo/schemas/grade/gradeCreate';
import type { GradeEditSchema } from '@repo/schemas/grade/gradeEdit';
import type { GradePublic, GradeWithCreator, GroupGradesResponse } from '@repo/types/grades';
import type { GradeValueType } from '@repo/types/grades';
import type { AxiosResponse } from 'axios';

import { axiosInstance } from '#api-client';

export class GradeService {
  static async getGrade(id?: number): Promise<AxiosResponse<GradeWithCreator>> {
    return await axiosInstance.get('/api/grade', { params: { id } });
  }

  static async getUserGrades(userId?: number): Promise<AxiosResponse<GradeWithCreator[]>> {
    return await axiosInstance.get(`/api/grade/user/${userId}`);
  }

  static async getGroupGrades(groupId?: number): Promise<AxiosResponse<GroupGradesResponse>> {
    return await axiosInstance.get(`/api/group/${groupId}/grades`);
  }

  static async getUserGroupGrades(userId?: number, groupId?: number): Promise<AxiosResponse<GradePublic[]>> {
    return await axiosInstance.get(`/api/user/${userId}/group/${groupId}/grades`);
  }

  static async createGrade(data: CreateGradeInput): Promise<AxiosResponse<GradePublic>> {
    return await axiosInstance.post('/api/grade', data);
  }

  static async editGrade(data: GradeEditData): Promise<AxiosResponse<GradePublic>> {
    return await axiosInstance.put('/api/grade', data);
  }

  static async deleteGrade(id: number): Promise<AxiosResponse<string>> {
    return await axiosInstance.delete('/api/grade', { data: { id } });
  }
}

export type GradeCreateData = GradeCreateSchema;

export type GradeEditData = GradeEditSchema;

export interface CreateGradeInput {
  name: string;
  desc?: string | null;
  valueType?: GradeValueType;
  value: string;
  /**
   * When set, the grade is tied to the assignment response: the backend stamps
   * originType 'assignmentResponse'/originId, links response.gradeId, and takes
   * the graded user from the response's author (so userId is ignored).
   * Re-grading the same response updates the existing grade in place.
   */
  respId?: number;
  userId?: number;
  groupId?: number | null;
}
